import { computed, ref, type Ref } from 'vue';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  primaryAction?: 'confirm' | 'cancel';
}

interface PromptOptions extends ConfirmOptions {
  inputLabel?: string;
  inputPlaceholder?: string;
  initialValue?: string;
  required?: boolean;
}

interface InternalConfirmOptions extends ConfirmOptions {
  input?: {
    label: string;
    placeholder: string;
    required: boolean;
  };
}

interface ConfirmState {
  isOpen: Ref<boolean>;
  inputValue: Ref<string>;
  options: Ref<InternalConfirmOptions | null>;
  resolve: ((value: boolean | string | null) => void) | null;
}

const state: ConfirmState = {
  isOpen: ref(false),
  inputValue: ref(''),
  options: ref(null),
  resolve: null,
};

/** Завершает ожидающий вызов один раз. Новый диалог заменяет открытый: тот получает отмену, а не висит вечно. */
const settle = (value: boolean | string | null): void => {
  state.resolve?.(value);
  state.resolve = null;
};

export const useConfirm = () => {
  const confirm = (options: string | ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      settle(null);
      const opts = typeof options === 'string' ? { message: options } : options;

      state.options.value = {
        title: opts.title ?? 'Подтверждение',
        message: opts.message,
        confirmText: opts.confirmText ?? 'Да',
        cancelText: opts.cancelText ?? 'Отмена',
        primaryAction: opts.primaryAction ?? 'confirm',
      };
      state.isOpen.value = true;
      state.resolve = (value) => resolve(value === true);
    });
  };

  const prompt = (options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      settle(null);
      state.inputValue.value = options.initialValue ?? '';
      state.options.value = {
        title: options.title ?? 'Подтверждение',
        message: options.message,
        confirmText: options.confirmText ?? 'Подтвердить',
        cancelText: options.cancelText ?? 'Отмена',
        primaryAction: options.primaryAction ?? 'confirm',
        input: {
          label: options.inputLabel ?? 'Причина',
          placeholder: options.inputPlaceholder ?? '',
          required: options.required ?? true,
        },
      };
      state.isOpen.value = true;
      state.resolve = (value) => resolve(typeof value === 'string' ? value : null);
    });
  };

  const handleConfirm = () => {
    if (state.options.value?.input) {
      const value = state.inputValue.value.trim();
      if (state.options.value.input.required && value === '') {
        return;
      }

      state.isOpen.value = false;
      settle(value);

      return;
    }

    state.isOpen.value = false;
    settle(true);
  };

  const handleCancel = () => {
    state.isOpen.value = false;
    settle(state.options.value?.input ? null : false);
  };

  const canConfirm = computed(() => !state.options.value?.input?.required || state.inputValue.value.trim() !== '');

  return {
    canConfirm,
    confirm,
    inputValue: state.inputValue,
    isOpen: state.isOpen,
    options: state.options,
    prompt,
    handleConfirm,
    handleCancel,
  };
};
